import type { Asset, AssetVariant, WorkMedia } from "@yushi-portfolio/content-schema";
import { label, pickNonEmpty, type Locale } from "./locale";

export type ImageSourceModel = { type: string; srcset: string };

export type ImageModel = {
  type: "image";
  src: string;
  srcset?: string;
  /** Additional formats, best first, for <picture><source>. */
  sources: ImageSourceModel[];
  width?: number;
  height?: number;
  aspectRatio?: number;
  alt: string;
  caption?: string;
  credit?: string;
};

export type VideoModel = {
  type: "video";
  sources: { src: string; type: string }[];
  poster?: string;
  width?: number;
  height?: number;
  aspectRatio?: number;
  /** Accessible description of the video. */
  label: string;
  caption?: string;
  credit?: string;
};

export type MediaModel = ImageModel | VideoModel;

export type MediaContext = {
  locale: Locale;
  /** Prefix for storage keys, without trailing slash. "" means same origin ("/web/..."). */
  mediaBaseUrl: string;
  assets: ReadonlyMap<string, Asset>;
};

const MIME: Record<NonNullable<AssetVariant["format"]>, string> = {
  avif: "image/avif",
  webp: "image/webp",
  jpeg: "image/jpeg",
  mp4: "video/mp4",
  webm: "video/webm",
};

const IMAGE_FORMATS = ["avif", "webp", "jpeg"] as const;
const VIDEO_FORMATS = ["webm", "mp4"] as const;
/** Width used for the plain `src` fallback when srcset is unsupported. */
const FALLBACK_TARGET_WIDTH = 1280;

export function mediaUrl(key: string, mediaBaseUrl: string): string {
  return `${mediaBaseUrl}/${key}`;
}

function srcsetOf(variants: AssetVariant[], base: string): string {
  return [...variants]
    .sort((a, b) => (a.width ?? 0) - (b.width ?? 0))
    .map((v) => (v.width ? `${mediaUrl(v.key, base)} ${v.width}w` : mediaUrl(v.key, base)))
    .join(", ");
}

function closestToWidth(variants: AssetVariant[], target: number): AssetVariant | undefined {
  return [...variants].sort(
    (a, b) => Math.abs((a.width ?? target) - target) - Math.abs((b.width ?? target) - target),
  )[0];
}

function creditOf(asset: Asset, locale: Locale): string | undefined {
  const parts = [
    asset.photographer ? `${label("photo", locale)}: ${asset.photographer}` : undefined,
    asset.copyright ? `© ${asset.copyright}` : undefined,
  ].filter(Boolean);
  return parts.length ? parts.join(" ") : undefined;
}

function dimensionsOf(asset: Asset) {
  const { width, height } = asset;
  return {
    width,
    height,
    aspectRatio: width && height ? width / height : undefined,
  };
}

function toImage(asset: Asset, media: WorkMedia, fallbackAlt: string, ctx: MediaContext): ImageModel | undefined {
  const variants = asset.variants ?? [];
  const groups = IMAGE_FORMATS.map((format) => ({
    format,
    variants: variants.filter((v) => v.format === format),
  })).filter((g) => g.variants.length > 0);
  // The most widely supported available format becomes <img>; the rest become <source>s.
  const fallback = groups.at(-1);
  if (!fallback) return undefined;
  const src = closestToWidth(fallback.variants, FALLBACK_TARGET_WIDTH);
  if (!src) return undefined;

  return {
    type: "image",
    src: mediaUrl(src.key, ctx.mediaBaseUrl),
    srcset: srcsetOf(fallback.variants, ctx.mediaBaseUrl),
    sources: groups.slice(0, -1).map((g) => ({ type: MIME[g.format], srcset: srcsetOf(g.variants, ctx.mediaBaseUrl) })),
    ...dimensionsOf(asset),
    alt: pickNonEmpty(media.alt, ctx.locale) ?? fallbackAlt,
    caption: pickNonEmpty(media.caption, ctx.locale),
    credit: creditOf(asset, ctx.locale),
  };
}

function toVideo(asset: Asset, media: WorkMedia, fallbackLabel: string, ctx: MediaContext): VideoModel | undefined {
  const variants = asset.variants ?? [];
  const sources = VIDEO_FORMATS.flatMap((format) =>
    variants.filter((v) => v.format === format).map((v) => ({ src: mediaUrl(v.key, ctx.mediaBaseUrl), type: MIME[format] })),
  );
  if (sources.length === 0) return undefined;
  // A still-image variant on a video asset serves as its poster frame.
  const poster = variants.find((v) => v.format === "jpeg" || v.format === "webp");

  return {
    type: "video",
    sources,
    poster: poster ? mediaUrl(poster.key, ctx.mediaBaseUrl) : undefined,
    ...dimensionsOf(asset),
    label: pickNonEmpty(media.alt, ctx.locale) ?? fallbackLabel,
    caption: pickNonEmpty(media.caption, ctx.locale),
    credit: creditOf(asset, ctx.locale),
  };
}

/**
 * Resolves a WorkMedia reference into something renderable.
 * Returns undefined for media that cannot be shown on the web (e.g. documents).
 */
export function toMediaModel(media: WorkMedia, fallbackAlt: string, ctx: MediaContext): MediaModel | undefined {
  const asset = ctx.assets.get(media.assetId);
  if (!asset) return undefined;
  if (asset.type === "image") return toImage(asset, media, fallbackAlt, ctx);
  if (asset.type === "video") return toVideo(asset, media, fallbackAlt, ctx);
  return undefined;
}

export function toMediaModels(media: readonly WorkMedia[], fallbackAlt: string, ctx: MediaContext): MediaModel[] {
  return media.flatMap((m) => toMediaModel(m, fallbackAlt, ctx) ?? []);
}
